import { useNavigate } from "react-router-dom";
import { Crown, SignOut, BookmarkSimple, Tray, Clock, SquaresFour, Receipt, Gift } from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Brand } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";

function PlanBadge({ user }) {
  const paid = user.plan_status === "active";
  const label = paid ? (user.plan === "team" ? "Team" : "Pro") : user.access ? `Trial · ${user.trial_days_left}d left` : "Trial ended";
  const cls = paid ? "bg-brand-soft text-brand" : user.access ? "bg-black/5 text-mist" : "bg-[#FFECEE] text-[#D70015]";
  return (
    <span data-testid="plan-badge" className={`hidden items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium sm:flex ${cls}`}>
      {paid ? <Crown size={13} weight="fill" /> : <Clock size={13} weight="bold" />} {label}
    </span>
  );
}

export function AppHeader({ onLibrary }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  if (!user) return null;
  const isAdmin = user.role === "admin";
  return (
    <header className="stp-glass sticky top-0 z-30 pt-[env(safe-area-inset-top)] border-x-0 border-t-0 border-b border-black/5" data-testid="app-header">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-5 sm:px-8">
        <Brand to="/app" />
        <div className="flex items-center gap-2">
          <PlanBadge user={user} />
          {user.plan_status !== "active" && (
            <button data-testid="upgrade-button" onClick={() => navigate("/pricing")} className="h-9 rounded-full bg-ink px-4 text-xs font-semibold text-white transition-colors hover:bg-black">
              Upgrade
            </button>
          )}
          {onLibrary && (
            <button data-testid="library-open-button" onClick={onLibrary} className="flex h-9 items-center gap-1.5 rounded-full bg-black/5 px-3.5 text-xs font-medium text-ink transition-colors hover:bg-black/10">
              <BookmarkSimple size={15} weight="fill" /> <span className="hidden sm:inline">Library</span>
            </button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-button" className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-brand text-sm font-semibold uppercase text-white">
                {user.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : user.name?.[0] || user.email?.[0]}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 rounded-2xl p-1.5">
              <div className="px-2 py-1.5 text-xs text-mist">{user.email}</div>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="menu-workspace" onClick={() => navigate("/app")} className="cursor-pointer rounded-lg"><SquaresFour size={15} className="mr-2" /> Workspace</DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem data-testid="menu-admin-inbox" onClick={() => navigate("/admin")} className="cursor-pointer rounded-lg"><Tray size={15} className="mr-2" /> Admin</DropdownMenuItem>
              )}
              <DropdownMenuItem data-testid="menu-billing" onClick={() => navigate("/billing")} className="cursor-pointer rounded-lg"><Receipt size={15} className="mr-2" /> Billing & receipts</DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-invite" onClick={() => navigate("/billing#invite")} className="cursor-pointer rounded-lg"><Gift size={15} className="mr-2" /> Invite friends, get Pro</DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-plans" onClick={() => navigate("/pricing")} className="cursor-pointer rounded-lg"><Crown size={15} className="mr-2" /> Plans</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="logout-button" onClick={async () => { await logout(); navigate("/login"); }} className="cursor-pointer rounded-lg"><SignOut size={15} className="mr-2" /> Sign out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
